import { BookOpen, Calculator, Timer, FileDown, Zap, Flame } from "lucide-react";

const FEATURES = [
  {
    icon: BookOpen,
    title: "The Handbook Section",
    text: "Head over to the specialist 'Athletes' section of your Member Handbook for a deep dive into midlife physiology, including how to manage the insulin sensitivity shift, protect joint integrity, and time your nutrition.",
  },
  {
    icon: Calculator,
    title: "Carb Sizing & Timing",
    text: "Clear guidelines on calculating your daily carbohydrate targets (grams per kg of body weight) based on your training volume—from light days to extreme endurance.",
  },
  {
    icon: Timer,
    title: "Peri-Workout Strategies",
    text: "Exactly what to eat before, during, and after your workouts to maximise performance and speed up recovery.",
  },
  {
    icon: FileDown,
    title: "Downloadable Cheat Sheets",
    text: "Practical, at-a-glance guides and meal ideas in the library to take the guesswork out of your training week.",
  },
];

export const AthletesHub = ({ children }) => (
  <div className="space-y-8" data-testid="athletes-hub">
    {/* intro */}
    <div className="bg-navy rounded-2xl p-8 sm:p-10 text-white relative overflow-hidden">
      <div className="brand-blob bg-teal" style={{ width: 300, height: 300, top: -80, right: -60 }} />
      <div className="relative max-w-3xl">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-teal">
          <Zap className="w-4 h-4" /> High-performance nutrition
        </span>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl">Welcome to the Athlete Hub</h2>
        <p className="mt-4 text-white/80 leading-relaxed">
          Your dedicated space for high-performance nutrition. If you are a mature female who takes your
          training seriously, you are by definition <em>&lsquo;An Athlete&rsquo;</em>. It&rsquo;s not about whether or not you
          are competitive, it&rsquo;s about the regular physical demands on your body&hellip; and hormones!
        </p>
        <p className="mt-4 text-white/70 leading-relaxed">
          A standard, generalized low-carbohydrate approach simply won&rsquo;t work; you will end up stressed,
          wired and tired. High-intensity endurance training demands smart energy pathways, and for women
          over 45 navigating changing hormones, fuelling requires a more nuanced approach.
        </p>
        <p className="mt-4 text-white/70 leading-relaxed">
          Here, we bridge the gap: teaching you how to properly fuel your hard sessions to prevent the
          dreaded &lsquo;bonk&rsquo; and support recovery, while preserving your body&rsquo;s efficiency as a natural fat burner.
        </p>
      </div>
    </div>

    {/* features */}
    <div>
      <h3 className="font-heading text-2xl text-navy">What You&rsquo;ll Find Inside</h3>
      <div className="mt-5 grid sm:grid-cols-2 gap-5">
        {FEATURES.map((f) => (
          <div key={f.title} className="bg-white border border-[#E7E5E4] rounded-2xl p-6 shadow-sm" data-testid={`athlete-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center"><f.icon className="w-5 h-5" /></div>
            <h4 className="mt-4 font-heading text-lg text-navy">{f.title}</h4>
            <p className="mt-1.5 text-sm text-[#57534E] leading-relaxed">{f.text}</p>
          </div>
        ))}
      </div>
    </div>

    {/* athlete content injected by parent */}
    {children}

    <div className="bg-teal/10 border border-teal/20 rounded-2xl p-6 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-teal text-white flex items-center justify-center shrink-0"><Flame className="w-5 h-5" /></div>
      <p className="font-heading text-lg text-navy">Fuel the engine right, protect your biology, and let&rsquo;s hit those performance goals.</p>
    </div>
  </div>
);
